import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeader } from './SectionHeader';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
const tomos = [{
  id: 'T1',
  title: 'Protocolo de Primer Contacto y Anamnesis Clínica',
  subtitle: 'La base que nadie te enseña en la facultad',
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=160&h=160&q=70&fit=crop&auto=format',
  alt: 'Profesional realizando entrevista de anamnesis',
  desc: 'Estructurá la entrevista inicial con los padres de principio a fin. Qué preguntar, en qué orden, qué datos son imprescindibles y cuáles podés dejar para después. Con guión de llamada inicial y ficha de anamnesis editable por rango etario.',
  bullets: ['Guión de llamada inicial con los padres', 'Ficha de anamnesis por edad (0–3 / 3–6 / 6–12 años)', 'Checklist de señales de alerta por etapa']
}, {
  id: 'T2',
  title: 'Árbol de Decisión Clínica: Qué Instrumento Usar y Cuándo',
  subtitle: 'Nunca más dudás entre TEPROSIF-R, PLON-R o TECAL',
  image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=160&h=160&q=70&fit=crop&auto=format',
  alt: 'Diagrama de decisión clínica para selección de tests',
  desc: 'Un sistema visual paso a paso para seleccionar la batería de evaluación correcta según el motivo de consulta, la edad y el perfil del paciente.',
  bullets: ['Diagramas de flujo por motivo de consulta', 'Fichas técnicas resumidas de los principales tests', 'Criterios de exclusión y selección rápida']
}, {
  id: 'T3',
  title: 'Protocolo de Sesión de Evaluación',
  subtitle: 'De la sala de espera al cierre de sesión — sin improvisar',
  image: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=160&h=160&q=70&fit=crop&auto=format',
  alt: 'Sesión de evaluación fonoaudiológica con niño',
  desc: 'Estructura detallada de cómo llevar adelante las sesiones de evaluación, manejando los tiempos, la fatiga del niño y la recolección de datos.',
  bullets: ['Estructura de sesión de 45 minutos optimizada', 'Estrategias de manejo conductual durante el testeo', 'Planilla de registro rápido de observaciones clínicas']
}, {
  id: 'T4',
  title: 'Cómo Redactar Informes Fonoaudiológicos con Autoridad',
  subtitle: 'De 2 horas a 30 minutos por informe',
  image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=160&h=160&q=70&fit=crop&auto=format',
  alt: 'Redacción de informe profesional sobre escritorio',
  desc: 'Plantillas estructuradas y un banco de frases para redactar informes profesionales, claros y directos sin perder horas frente a la pantalla.',
  bullets: ['Plantillas editables para diferentes diagnósticos', 'Banco de 80 frases clínicas para describir resultados', 'Estructura recomendada para obras sociales y escuelas']
}, {
  id: 'T5',
  title: 'Protocolo de Devolución Diagnóstica a las Familias',
  subtitle: 'Comunicá con claridad, sin tecnicismos y sin generar angustia',
  image: 'https://images.unsplash.com/photo-1581579438747-104c53e7ddec?w=160&h=160&q=70&fit=crop&auto=format',
  alt: 'Devolución diagnóstica a familia',
  desc: 'Guiones y estructuras para la entrevista de devolución, asegurando que los padres comprendan el diagnóstico y se comprometan con el tratamiento.',
  bullets: ['Estructura de la entrevista de devolución paso a paso', 'Guión para comunicar diagnósticos complejos', 'Material de apoyo visual para explicar a los padres']
}, {
  id: 'T6',
  title: 'Coordinación con la Escuela y el Equipo Interdisciplinario',
  subtitle: 'Articulá con docentes, psicólogos y pediatras',
  image: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=160&h=160&q=70&fit=crop&auto=format',
  alt: 'Aula escolar con materiales educativos',
  desc: 'Herramientas para comunicarte efectivamente con otros profesionales y la escuela, posicionándote como una referente en tu área.',
  bullets: ['Modelo de informe escolar simplificado', 'Guía para reuniones de equipo interdisciplinario', 'Plantilla de derivación y solicitud de interconsulta']
}, {
  id: 'T7',
  title: 'Cómo Armar y Gestionar tu Consultorio Privado desde Cero',
  subtitle: 'Honorarios, cancelaciones, agenda y acuerdo de inicio',
  image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=160&h=160&q=70&fit=crop&auto=format',
  alt: 'Calculadora y planificación financiera del consultorio',
  desc: 'Los cimientos administrativos de tu práctica: desde cómo fijar tus honorarios hasta cómo manejar las inasistencias sin perder dinero ni pacientes.',
  bullets: ['Calculadora de honorarios y costos fijos', 'Modelo de acuerdo de inicio y política de cancelaciones', 'Guión profesional para comunicar aumentos de precio']
}, {
  id: 'T8',
  title: 'Cómo Comunicar tu Trabajo en Redes y Conseguir Pacientes',
  subtitle: 'Instagram, derivaciones, escuelas y Google My Business',
  image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=160&h=160&q=70&fit=crop&auto=format',
  alt: 'Comunicación profesional en redes sociales',
  desc: 'Estrategias orgánicas y éticas para dar a conocer tu trabajo, generar confianza y llenar tu agenda sin depender de publicidad paga.',
  bullets: ['Calendario de 30 días de contenido para Instagram', 'Guión de presentación efectiva ante pediatras', 'Estrategia de posicionamiento en Google My Business']
}];
export function TomosAccordion() {
  const [openId, setOpenId] = useState<string>('T1');
  return <section className="py-12 md:py-20 px-4 md:px-8 bg-cream" data-id="element-303">
      <div className="max-w-3xl mx-auto" data-id="element-304">
        <SectionHeader eyebrow="CONTENIDO COMPLETO" title="LOS 8 TOMOS" subtitle="Tu hoja de ruta para evaluar con seguridad y criterio propio" className="mb-8 md:mb-12" data-id="element-305" />

        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100" data-id="element-306">
          {tomos.map((tomo, index) => {
          const isOpen = openId === tomo.id;
          return <motion.div key={tomo.id} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-30px'
          }} transition={{
            duration: 0.4,
            delay: index * 0.04
          }} className={`transition-colors ${isOpen ? 'bg-cream/40' : 'hover:bg-cream/30'}`} data-id="element-307">
                <button onClick={() => setOpenId(isOpen ? '' : tomo.id)} className="w-full text-left p-3 md:p-5 flex items-start gap-3 md:gap-5 focus:outline-none" data-id="element-308">
                  {/* Image thumbnail */}
                  <div className="shrink-0 relative" data-id="element-309">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-teal-50 border border-slate-200" data-id="element-310">
                      <img src={tomo.image} alt={tomo.alt} width="80" height="80" loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-311" />
                    </div>
                    <span className={`absolute -top-1.5 -left-1.5 font-bold text-[10px] md:text-xs px-1.5 py-0.5 rounded shadow uppercase tracking-wider transition-colors ${isOpen ? 'bg-teal-900 text-white' : 'bg-white text-teal-900 border border-teal-200'}`} data-id="element-312">
                      {tomo.id}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-grow min-w-0 pt-0.5" data-id="element-313">
                    <h3 className="text-sm md:text-base font-bold text-teal-900 mb-0.5 md:mb-1 font-serif leading-tight" data-id="element-314">
                      {tomo.title}
                    </h3>
                    <p className="text-slate-500 text-xs md:text-sm font-medium leading-snug" data-id="element-315">
                      {tomo.subtitle}
                    </p>
                  </div>

                  {/* Chevron */}
                  <div className={`shrink-0 mt-1 transition-transform duration-300 ${isOpen ? 'rotate-180 text-terracotta' : 'text-slate-400'}`} data-id="element-316">
                    <ChevronDownIcon size={18} data-id="element-317" />
                  </div>
                </button>

                <AnimatePresence data-id="element-318">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-319">
                      <div className="px-3 md:px-5 pb-4 md:pb-5 pt-0 md:pl-[6.25rem]" data-id="element-320">
                        <p className="text-xs md:text-sm text-slate-700 mb-3 leading-relaxed" data-id="element-321">
                          {tomo.desc}
                        </p>
                        <ul className="space-y-2" data-id="element-322">
                          {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2 text-slate-600" data-id="element-323">
                              <CheckIcon className="text-terracotta shrink-0 mt-0.5" size={14} data-id="element-324" />
                              <span className="text-xs md:text-sm font-medium leading-snug" data-id="element-325">
                                {bullet}
                              </span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        <p className="text-center text-xs md:text-sm text-slate-500 font-medium mt-6 md:mt-8" data-id="element-326">
          Tocá cada tomo para ver lo que incluye 👆
        </p>
      </div>
    </section>;
}